import { useContext, useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { TailSpin } from 'react-loader-spinner';
import { useParams } from 'react-router-dom';

import { BudgetProductType } from 'types/api/budgets.type';

import { useAppSelector } from 'redux/hooks';

import { budgetDataService } from 'services/budget.service';

import { BudgetContext } from 'contexts';

import { useFetch } from 'hooks';

import { checkParams } from 'helpers/checkParams';

import { BudgetListSkeleton } from '../BudgetListSkeleton';
import { BudgetProduct } from '../BudgetProduct';
import * as S from './Code.styles';

const PAGE_SIZE = 20;

type CodeProps = {
  /** False while the tab is kept mounted but hidden: no infinite-scroll loading then. */
  isActive?: boolean;
};

export function Code({ isActive = true }: CodeProps): React.ReactElement {
  const { id, gender } = useParams();

  const { season, zone, marketLine, warehouse } = useAppSelector((state) => state.settings);
  const { addProducts, params: contextParams } = useContext(BudgetContext);

  const params = {
    season,
    zone,
    seasonLine: marketLine,
    warehouse: warehouse.code,
    budgetNum: id!,
    genderCode: gender!,
    type: contextParams?.type || '',
  };

  // Track product IDs for rendering order (Map doesn't guarantee order)
  const [productIds, productIdsSet] = useState<string[]>([]);
  // Codes whose products haven't been requested yet (consumed PAGE_SIZE at a time)
  const pendingCodes = useRef<string[]>([]);
  const isFetchingMore = useRef(false);
  const [hasMore, hasMoreSet] = useState(false);
  const [isFirstBatchLoaded, isFirstBatchLoadedSet] = useState(false);

  const { content, isLoading } = useFetch(
    checkParams(params) ? () => budgetDataService.getCodes(params) : undefined,
    [season, zone, marketLine, warehouse, id, gender, contextParams?.type]
  );

  const fetchProducts = (codes: string[]) =>
    budgetDataService.getProductCode(params, codes).then((res) => {
      // Add products to context
      addProducts(res.data);
      // Track IDs for render order
      return res.data.map((item: BudgetProductType) => item.mainProduct.id) as string[];
    });

  // First page straight from the codes response (no extra render passes before the request)
  useEffect(() => {
    if (!content) return undefined;

    let isCurrent = true;
    pendingCodes.current = [...content];
    const firstPage = pendingCodes.current.splice(0, PAGE_SIZE);
    productIdsSet([]);
    hasMoreSet(pendingCodes.current.length > 0);
    isFirstBatchLoadedSet(firstPage.length === 0);

    if (firstPage.length > 0) {
      fetchProducts(firstPage)
        .then((ids) => {
          if (isCurrent) productIdsSet(ids);
        })
        .finally(() => {
          if (isCurrent) isFirstBatchLoadedSet(true);
        });
    }

    return () => {
      isCurrent = false;
    };
  }, [content]);

  const addDataOnScroll = () => {
    // One page at a time, and only after the first page is in
    if (!isFirstBatchLoaded || isFetchingMore.current || pendingCodes.current.length === 0) {
      return;
    }

    isFetchingMore.current = true;
    const nextPage = pendingCodes.current.splice(0, PAGE_SIZE);
    hasMoreSet(pendingCodes.current.length > 0);

    fetchProducts(nextPage)
      .then((ids) => productIdsSet((state) => [...state, ...ids]))
      .finally(() => {
        isFetchingMore.current = false;
      });
  };

  const dataMapped = productIds.map((productId) => (
    <BudgetProduct key={productId} productId={productId} />
  ));

  // Codes first, then the first page of products: show the skeleton across both requests
  if (isLoading || (content !== undefined && !isFirstBatchLoaded)) {
    return <BudgetListSkeleton />;
  }

  return (
    <InfiniteScroll
      hasMore={hasMore && isActive}
      loadMore={() => addDataOnScroll()}
      loader={
        <S.LoadingContainer key="loader">
          <TailSpin width="100" color="#286090" />
        </S.LoadingContainer>
      }
    >
      {dataMapped}
    </InfiniteScroll>
  );
}
